import type { ReactNode } from "react";
import { Link } from "react-router";
import { FormattedMessage } from "react-intl";
import { PauseCircle } from "lucide-react";

import { EmptyCard } from "@/components/common";
import { buildBrandPath } from "@/hooks/use-route-brand";
import { Button, Card, CardContent } from "@runcheetah/ui";

type CampaignAutopilotNoticeProps = {
  brandId: string;
};

const settingsLinkClassName =
  "font-medium text-foreground underline underline-offset-4 hover:text-foreground/75";

function CampaignAutopilotSettingsLink({
  brandId,
  children,
  className = settingsLinkClassName,
}: CampaignAutopilotNoticeProps & {
  children: ReactNode;
  className?: string;
}) {
  return (
    <Link to={buildBrandPath(brandId, "/settings")} className={className}>
      {children}
    </Link>
  );
}

export function CampaignAutopilotPausedEmptyState({ brandId }: CampaignAutopilotNoticeProps) {
  return (
    <EmptyCard contentClassName="gap-4 px-6">
      <div className="max-w-xl space-y-2">
        <h2 className="text-base font-semibold text-foreground">
          <FormattedMessage
            id="campaigns.autopilotPaused.emptyTitle"
            defaultMessage="Campaigns are manual while Autopilot is off"
          />
        </h2>
        <p className="text-sm leading-6 text-muted-foreground">
          <FormattedMessage
            id="campaigns.autopilotPaused.emptyDescription"
            defaultMessage="Campaigns can only be manually generated while Autopilot is off. Turn it back on in <settingsLink>Autopilot settings</settingsLink>."
            values={{
              settingsLink: (chunks) => (
                <CampaignAutopilotSettingsLink brandId={brandId}>
                  {chunks}
                </CampaignAutopilotSettingsLink>
              ),
            }}
          />
        </p>
      </div>
    </EmptyCard>
  );
}

export function CampaignAutopilotPausedBanner({ brandId }: CampaignAutopilotNoticeProps) {
  return (
    <Card className="overflow-hidden rounded-3xl border-brand/30 bg-brand/5 shadow-none">
      <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-2xl bg-brand/10 text-brand">
            <PauseCircle className="size-5" aria-hidden="true" />
          </span>
          <div className="space-y-1">
            <h2 className="text-base font-bold text-primary">
              <FormattedMessage
                id="campaigns.autopilotPaused.bannerTitle"
                defaultMessage="Autopilot is off"
              />
            </h2>
            <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
              <FormattedMessage
                id="campaigns.autopilotPaused.bannerDescription"
                defaultMessage="Campaigns can only be manually generated while Autopilot is off."
              />
            </p>
          </div>
        </div>
        <Button asChild variant="outline" size="sm" className="w-fit rounded-xl">
          <Link to={buildBrandPath(brandId, "/settings")}>
            <FormattedMessage
              id="campaigns.autopilotPaused.settingsLink"
              defaultMessage="Autopilot settings"
            />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
